import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import React, { useState, useEffect } from 'react';
import jsPDF from 'jspdf';
import ResetButton from './components/ResetButton';
import './theme.css';
import './styles/App.css';
import { Preferences } from '@capacitor/preferences';
import * as XLSX from 'xlsx';

// Utility to detect if running in a Capacitor native environment
const isNative = Capacitor.getPlatform() !== 'web';
const OPTION_LISTS_PATH = 'bingo/optionLists.json';

type Option = { id: string; value: string };

// ID generator with crypto fallback
const generateId = (): string => {
  try {
    // @ts-ignore
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      // @ts-ignore
      return crypto.randomUUID();
    }
  } catch {}
  return Math.random().toString(36).slice(2) + Date.now();
};

const makeOption = (value: string): Option => ({ id: generateId(), value: value.trim() });

const toColumnName = (index: number): string => {
  let name = '';
  let i = index;
  while (i >= 0) {
    name = String.fromCharCode(65 + (i % 26)) + name;
    i = Math.floor(i / 26) - 1;
  }
  return name;
};

const parseBufferToRows = (buf: ArrayBuffer): string[][] => {
  const wb = XLSX.read(buf, { type: 'array', raw: true });
  const sheetName = wb.SheetNames[0];
  const ws = wb.Sheets[sheetName];
  const rows: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1 });
  return rows.map(row =>
    (row || []).map(cell => {
      if (typeof cell === 'string') return cell.trim();
      if (typeof cell === 'number') return String(cell);
      return '';
    })
  );
};

const buildOptionsFromRows = (rows: string[][], colIdx: number): Option[] => {
  const values: string[] = [];
  for (const row of rows) {
    const cell = row[colIdx];
    if (!cell) continue;
    const trimmed = cell.trim();
    if (trimmed.length === 0) continue;
    values.push(trimmed);
  }
  return values.map(v => makeOption(v));
};

const defaultOptionsText = [
  'B1', 'B2', 'B3', 'B4', 'B5',
  'I16', 'I17', 'I18', 'I19', 'I20',
  'N31', 'N32', 'N33', 'N34', 'N35',
  'G46', 'G47', 'G48', 'G49', 'G50',
  'O61', 'O62', 'O63', 'O64', 'O65'
];

// Use value as id for built-in defaults (stable, readable)
const defaultOptions: Option[] = defaultOptionsText.map(value => ({ id: value, value }));

type Screen = 'menu' | 'selectGame' | 'game' | 'customize' | 'generateCards' | 'viewCards' | 'editGameLists' | 'importList' | 'settings';
type OptionList = {
  name: string;
  options: Option[];
};

const App: React.FC = () => {
  // Theme state
  const [theme, setTheme] = useState<'default' | 'night'>('default');
  // Apply theme to document root
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);
  // Import List state
  const [importListName, setImportListName] = useState('');
  const [importListText, setImportListText] = useState('');
  const [importFileOptions, setImportFileOptions] = useState<Option[]>([]);
  const [importError, setImportError] = useState<string>('');
  const [importParsedRows, setImportParsedRows] = useState<string[][]>([]);
  const [importColumnIndex, setImportColumnIndex] = useState<number>(0);
  // Helper to convert Blob to base64
  const blobToBase64 = (blob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64data = (reader.result as string).split(',')[1];
        resolve(base64data);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  // Save/share PDF on mobile
  const saveOrSharePDFMobile = async (doc: jsPDF, filename: string) => {
    const pdfBlob = doc.output('blob');
    const base64 = await blobToBase64(pdfBlob);
    const filePath = `bingo/${filename}`;
    const result = await Filesystem.writeFile({
      path: filePath,
      data: base64,
      directory: Directory.Documents,
      recursive: true,
    });
    // Only share if not web
    if (Capacitor.getPlatform() !== 'web') {
      await Share.share({
        title: 'Bingo Cards PDF',
        text: 'Your generated bingo cards PDF.',
        url: result.uri, // Use the actual file URI returned by Filesystem
        dialogTitle: 'Share Bingo Cards PDF',
      });
    }
  };

  // All useState hooks at the top
  const [screen, setScreen] = useState<Screen>('menu');
  const [optionLists, setOptionLists] = useState<OptionList[]>([]);
  const [selectedListIndex, setSelectedListIndex] = useState<number | null>(null);
  const [calledIds, setCalledIds] = useState<string[]>([]);
  const [lastCalledText, setLastCalledText] = useState<string | null>(null);
  const [cardsGameIdx, setCardsGameIdx] = useState<number | null>(null);
  const [numCards, setNumCards] = useState<number>(1);
  const [numCardsInput, setNumCardsInput] = useState<string>('1');
  const [generatedCards, setGeneratedCards] = useState<string[][][]>([]);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [initialCustomList, setInitialCustomList] = useState<Option[]>([]);
  const [initialListName, setInitialListName] = useState<string>('');
  const [unsavedChanges, setUnsavedChanges] = useState<boolean>(false);
  const [customList, setCustomList] = useState<Option[]>([]);
  const [customInput, setCustomInput] = useState<string>('');
  const [listName, setListName] = useState<string>('');
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [editOptionIndex, setEditOptionIndex] = useState<number | null>(null);
  const [editListIndex, setEditListIndex] = useState<number | null>(null);
  const [longPressTimer, setLongPressTimer] = useState<NodeJS.Timeout | null>(null);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [modalIdx, setModalIdx] = useState<number | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [mainMenuModalOpen, setMainMenuModalOpen] = useState(false);
  const [discardImportModalOpen, setDiscardImportModalOpen] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  // Normalize lists from storage (handles legacy string arrays)
  const normalizeOptionLists = (lists: any): OptionList[] => {
    if (!Array.isArray(lists) || lists.length === 0) {
      return [{ name: 'Sample List', options: defaultOptions }];
    }
    return lists.map((list: any) => {
      const name = typeof list?.name === 'string' ? list.name : 'Unnamed List';
      const optsRaw = Array.isArray(list?.options) ? list.options : [];
      const options: Option[] = optsRaw
        .map((o: any) => {
          if (typeof o === 'string') return makeOption(o);
          if (o && typeof o.id === 'string' && typeof o.value === 'string') return o as Option;
          return null;
        })
        .filter(Boolean) as Option[];
      return { name, options: options.length > 0 ? options : defaultOptions };
    });
  };

  // Load optionLists from Filesystem (native) or localStorage (web) on mount
  useEffect(() => {
    (async () => {
      if (isNative) {
        try {
          const res = await Filesystem.readFile({
            path: OPTION_LISTS_PATH,
            directory: Directory.Documents,
          });
          const jsonStr = typeof res.data === 'string' ? res.data : await (res.data as Blob).text();
          const parsed = JSON.parse(jsonStr);
          setOptionLists(normalizeOptionLists(parsed));
        } catch {
          const { value } = await Preferences.get({ key: 'optionLists' });
          if (value) {
            const parsed = JSON.parse(value);
            setOptionLists(normalizeOptionLists(parsed));
          } else {
            setOptionLists([{ name: 'Sample List', options: defaultOptions }]);
          }
        }
      } else {
        const value = localStorage.getItem('optionLists');
        if (value) {
          const parsedValue = JSON.parse(value);
          const normalized = normalizeOptionLists(parsedValue);
          if (normalized.length > 0) {
            setOptionLists(normalized);
          } else {
            setOptionLists([{ name: 'Sample List', options: defaultOptions }]);
          }
        } else {
          setOptionLists([{ name: 'Sample List', options: defaultOptions }]);
        }
      }
      setIsLoaded(true);
    })();
  }, []);

  // Save optionLists to Filesystem (native) or localStorage (web) whenever it changes
  useEffect(() => {
    if (!isLoaded) return; // Don't save until initial load is complete
    (async () => {
      if (isNative) {
        await Preferences.set({
          key: 'optionLists',
          value: JSON.stringify(optionLists),
        });
        try {
          await Filesystem.writeFile({
            path: OPTION_LISTS_PATH,
            data: JSON.stringify(optionLists),
            directory: Directory.Documents,
            recursive: true,
          });
        } catch (e) {
          console.warn('Failed to write option lists to Filesystem:', e);
        }
      } else {
        localStorage.setItem('optionLists', JSON.stringify(optionLists));
      }
    })();
  }, [optionLists, isLoaded]);

  // Set initial values when entering customize screen
  useEffect(() => {
    if (screen === 'customize') {
      setInitialCustomList([...customList]);
      setInitialListName(listName);
      setCustomInput('');
      setUnsavedChanges(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen]);

  // Mark unsaved changes if listName, customList, or customInput changes (after initial values are set)
  useEffect(() => {
    if (screen === 'customize') {
      setUnsavedChanges(
        listName !== initialListName ||
        JSON.stringify(customList) !== JSON.stringify(initialCustomList) ||
        customInput.trim().length > 0
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen, listName, customList, customInput, initialListName, initialCustomList]);

  // All handler functions here
  const handleCall = () => {
    const remaining = options.filter((opt: Option) => !calledIds.includes(opt.id));
    if (remaining.length === 0) return;
    const randomOpt = remaining[Math.floor(Math.random() * remaining.length)];
    setCalledIds([...calledIds, randomOpt.id]);
    setLastCalledText(randomOpt.value);
  };

  const handleReset = () => {
    setCalledIds([]);
    setLastCalledText(null);
  };

  // Update to use selected option list
  const options =
    selectedListIndex !== null && optionLists[selectedListIndex]
      ? optionLists[selectedListIndex].options
      : [];
  const optionMap = new Map(options.map(o => [o.id, o.value]));

  const handleAddOption = () => {
    if (customInput.trim().length === 0) return;
    if (editOptionIndex !== null) {
      const updated = [...customList];
      updated[editOptionIndex] = { ...updated[editOptionIndex], value: customInput.trim() };
      setCustomList(updated);
      setEditOptionIndex(null);
    } else {
      setCustomList([...customList, makeOption(customInput)]);
    }
    setCustomInput('');
    setUnsavedChanges(true);
  };

  const handleClearInput = () => setCustomInput('');

  const handleDeleteOption = (idx: number) => {
    setCustomList(customList.filter((_, i) => i !== idx));
    if (editIndex === idx) setEditIndex(null);
  };

  const handleEditOption = (idx: number) => {
    setCustomInput(customList[idx].value);
    setEditOptionIndex(idx);
  };

  const openActionModal = (idx: number) => {
    setModalIdx(idx);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalIdx(null);
  };

  const handleEditFromModal = () => {
    if (modalIdx !== null) handleEditOption(modalIdx);
    closeModal();
  };

  const handleDeleteFromModal = () => {
    if (modalIdx !== null) handleDeleteOption(modalIdx);
    closeModal();
  };

  const handleDeleteGame = (idx: number) => {
    setOptionLists(optionLists.filter((_, i) => i !== idx));
    setScreen('editGameLists');
    setDeleteModalOpen(false);
  };

  // Long-press handler for mobile
  const handleTouchStart = (idx: number) => {
    const timer = setTimeout(() => openActionModal(idx), 600);
    setLongPressTimer(timer);
  };

  const handleTouchEnd = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
  };

  // Desktop right-click context menu
  const handleContextMenu = (e: React.MouseEvent, idx: number) => {
    e.preventDefault();
    openActionModal(idx);
  };

  // Add new list
  const handleSaveList = () => {
    // Handle text in the input box before saving
    let finalList = [...customList];
    if (customInput.trim().length > 0) {
      if (editOptionIndex !== null) {
        // If editing an existing option, update it
        finalList[editOptionIndex] = { ...finalList[editOptionIndex], value: customInput.trim() };
      } else {
        // If not editing, add as new option
        finalList = [...customList, makeOption(customInput.trim())];
      }
    }

    if (listName.trim() && finalList.length > 0) {
      // Always append a new list, never overwrite
      // Warn if name already exists
      const nameExists = optionLists.some(list => list.name.trim().toLowerCase() === listName.trim().toLowerCase());
      if (editListIndex === null) {
        if (nameExists) {
          alert('A list with this name already exists. Please choose a different name.');
          return;
        }
        setOptionLists([...optionLists, { name: listName.trim(), options: finalList }]);
      } else {
        // Edit existing list
        const updatedLists = [...optionLists];
        updatedLists[editListIndex] = { name: listName.trim(), options: finalList };
        setOptionLists(updatedLists);
      }
      // Update the current state with saved values
      setCustomList(finalList);
      setCustomInput('');
      setEditOptionIndex(null);
      setUnsavedChanges(false); // Reset unsaved changes after saving
      setInitialCustomList(finalList); // Also update initial values for next change detection
      setInitialListName(listName.trim());
      // Don't reset listName, customList, or change screen - stay on customize screen
    }
  };

  // Select a list to play
  const handleSelectList = (idx: number) => {
    setSelectedListIndex(idx);
    setCalledIds([]);
    setLastCalledText(null);
    setScreen('game');
  };

  const handleSavePDF = () => {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'pt',
      format: 'letter'
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const cellSize = 90; // was 60
    const gridWidth = cellSize * 5;
    const startX = (pageWidth - gridWidth) / 2; // Center the grid horizontally
    const startY = 80;

    generatedCards.forEach((card, idx) => {
      if (idx > 0) doc.addPage();

      const gameName = cardsGameIdx !== null ? optionLists[cardsGameIdx].name : 'BINGO';
      doc.setFontSize(20);
      doc.text(`${gameName} Card ${idx + 1}`, pageWidth / 2, 50, { align: 'center' });

      // Draw grid
      for (let row = 0; row < 5; row++) {
        for (let col = 0; col < 5; col++) {
          const x = startX + col * cellSize;
          const y = startY + row * cellSize;

          // Draw cell border
          doc.setDrawColor(0);
          doc.rect(x, y, cellSize, cellSize);

          // Fill FREE cell
          if (card[row][col] === 'FREE') {
            doc.setFillColor(200, 255, 200);
            doc.rect(x, y, cellSize, cellSize, 'F');
          }

          // Draw text
          const cellPadding = 6;
          const maxTextWidth = cellSize - cellPadding * 2;

          let text = card[row][col];
          let fontSize = text === 'FREE' ? 16 : 12;
          doc.setFontSize(fontSize);
          doc.setFont(text === 'FREE' ? 'helvetica' : 'times', text === 'FREE' ? 'bold' : 'normal');

          // Split text to fit cell
          const lines = doc.splitTextToSize(text, maxTextWidth);

          // Calculate vertical offset to center the text block
          const lineHeight = fontSize + 2;
          const textBlockHeight = lines.length * lineHeight;
          const yOffset = y + (cellSize - textBlockHeight) / 2 + fontSize;

          // Draw each line
          lines.forEach((line: string, i: number) => {
            doc.text(
              line,
              x + cellSize / 2,
              yOffset + i * lineHeight,
              { align: 'center', baseline: 'top' }
            );
          });
        }
      }
    });

    if (Capacitor.getPlatform() !== 'web') {
      saveOrSharePDFMobile(doc, 'bingo-cards.pdf');
    } else {
      doc.save('bingo-cards.pdf');
    }
  };

  const generateCards = () => {
    if (cardsGameIdx === null) return;
    const options = optionLists[cardsGameIdx].options;
    const cards: string[][][] = [];
    for (let c = 0; c < numCards; c++) {
      // Shuffle options for each card
      const shuffled = [...options].sort(() => Math.random() - 0.5);
      let idx = 0;
      const grid: string[][] = [];
      for (let row = 0; row < 5; row++) {
        const gridRow: string[] = [];
        for (let col = 0; col < 5; col++) {
          if (row === 2 && col === 2) {
            gridRow.push('FREE');
          } else {
            gridRow.push(shuffled[idx]?.value || '');
            idx++;
          }
        }
        grid.push(gridRow);
      }
      cards.push(grid);
    }
    setGeneratedCards(cards);
  };

  // --- Main Menu ---
  if (screen === 'menu') {
    return (
      <div className="centered-form menu-centered-form">
        <h1>BINGO Tracker</h1>
        <div className="main-menu-buttons">
          <button onClick={() => { setSelectedListIndex(null); setScreen('selectGame'); }}>Start Game</button>
          <button onClick={() => setScreen('editGameLists')}>Edit Game Option Lists</button>
          <button onClick={() => setScreen('generateCards')}>Generate Cards</button>
          <button onClick={() => setScreen('settings')}>Settings</button>
        </div>
      </div>
    );
  }

      // --- Settings Screen ---
      if (screen === 'settings') {
        return (
          <div className="centered-form">
            <h1>Settings</h1>
            <div style={{ margin: '16px 0' }}>
              <label htmlFor="theme-select">Color Scheme:</label>
              <select
                id="theme-select"
                value={theme}
                onChange={e => setTheme(e.target.value as 'default' | 'night')}
                style={{ marginLeft: 8 }}
              >
                <option value="default">Default</option>
                <option value="dark">Dark Mode</option>
              </select>
            </div>
            <button onClick={() => setScreen('menu')}>Back to Main Menu</button>
          </div>
        );
      }

  // --- Select Game ---
  if (screen === 'selectGame') {
    return (
      <div className="centered-form">
        <h1>Select a Game</h1>
        <div className="game-listbox">
          <ul>
            {optionLists.map((list, idx) => (
              <li key={idx}>
                <button
                  style={{
                    fontWeight: selectedListIndex === idx ? 'bold' : 'normal',
                    background: selectedListIndex === idx ? '#e0e0e0' : undefined
                  }}
                  onClick={() => {
                    setSelectedListIndex(idx);
                    setCalledIds([]);
                    setLastCalledText(null);
                    setScreen('game');
                  }}
                >
                  {list.name}
                </button>
              </li>
            ))}
          </ul>
        </div>
        <button onClick={() => setScreen('menu')} style={{ marginLeft: 8 }}>
          Back to Main Menu
        </button>
      </div>
    );
  }

  // --- Edit Game Option Lists ---
  if (screen === 'editGameLists') {
    return (
      <div className="centered-form">
        <h1>Edit Game Option Lists</h1>
        <h2>Saved Games</h2>
        <div className="game-listbox">
          <ul>
            {optionLists.map((list, idx) => (
              <li key={idx}>
                <button
                  onClick={() => {
                    setSelectedListIndex(idx);
                    setEditListIndex(idx); // idx is the list index
                    setCustomList(list.options);
                    setListName(list.name);
                    setScreen('customize');
                  }}
                >
                  {list.name}
                </button>
              </li>
            ))}
          </ul>
        </div>
        <button
          onClick={() => {
            setSelectedListIndex(null);
            setCustomList([]);
            setListName('');
            setEditIndex(null);
            setEditListIndex(null);
            setScreen('customize');
          }}
          style={{ marginTop: 16 }}
        >
          Add New Options List
        </button>
        <button
          onClick={() => {
            setImportListName('');
            setImportListText('');
            setScreen('importList');
          }}
          style={{ marginTop: 8 }}
        >
          Import List
        </button>
        <button onClick={() => setScreen('menu')} style={{ marginLeft: 8 }}>
          Back to Main Menu
        </button>
      </div>
    );
  }

  // --- Import List ---
  if (screen === 'importList') {
    return (
      <div className="centered-form">
        <h1>Import Game Option List</h1>
        <input
          type="text"
          className="input-textbox"
          value={importListName}
          onChange={e => setImportListName(e.target.value)}
          placeholder="List Name"
          style={{ width: '200px' }}
        />
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 4 }}>Upload Excel/CSV (.xlsx/.xls/.csv)</label>
          <input
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={async e => {
              setImportError('');
              const file = e.target.files?.[0];
              if (!file) return;
              try {
                const buf = await file.arrayBuffer();
                const rows = parseBufferToRows(buf);
                setImportParsedRows(rows);
                setImportColumnIndex(0);
                const options = buildOptionsFromRows(rows, 0);
                setImportFileOptions(options);
                // Populate textarea with imported data
                const importedText = options.map(o => o.value).join(',\n');
                setImportListText(importedText);
              } catch (err: any) {
                console.error(err);
                setImportError('Failed to parse file. Please check the format.');
              }
            }}
          />
          {importError && (
            <div style={{ color: 'var(--danger-color)', marginTop: 6 }}>{importError}</div>
          )}
          {importParsedRows.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <div style={{ marginBottom: 6 }}>
                <label htmlFor="column-select" style={{ marginRight: 6 }}>Column:</label>
                <select
                  id="column-select"
                  value={importColumnIndex}
                  onChange={e => {
                    const idx = Number(e.target.value);
                    setImportColumnIndex(idx);
                    const options = buildOptionsFromRows(importParsedRows, idx);
                    setImportFileOptions(options);
                    // Update textarea with new column data
                    const importedText = options.map(o => o.value).join(',\n');
                    setImportListText(importedText);
                  }}
                >
                  {(() => {
                    const maxCols = importParsedRows.reduce((max, row) => Math.max(max, row.length), 0);
                    const headerRow = importParsedRows[0] || [];
                    return Array.from({ length: maxCols }, (_, i) => {
                      const header = headerRow[i];
                      const label = header ? `${toColumnName(i)} (${header})` : toColumnName(i);
                      return (
                        <option key={i} value={i}>{label}</option>
                      );
                    });
                  })()}
                </select>
              </div>
              Parsed options: {importFileOptions.length}
              <button
                style={{ marginLeft: 8 }}
                onClick={() => {
                  setImportFileOptions([]);
                  setImportParsedRows([]);
                  setImportColumnIndex(0);
                  setImportListText('');
                }}
              >
                Clear File
              </button>
            </div>
          )}
        </div>
        <div
          className="game-listbox"
          style={{ marginBottom: 16 }}
        >
          <textarea
            className="import-textarea"
            value={importListText}
            onChange={e => setImportListText(e.target.value)}
            placeholder="Paste comma-separated values here, ex: B1,B2,B3,..."
            style={{width: '300px'}}
          />
        </div>
        <button
          onClick={() => {
            if (!importListName.trim()) return;
            let options: Option[] = [];
            if (importFileOptions.length > 0) {
              options = importFileOptions;
            } else {
              if (!importListText.trim()) return;
              options = importListText
                .split(',')
                .map(opt => opt.trim())
                .filter(opt => opt.length > 0)
                .map(makeOption);
            }
            if (options.length === 0) return;
            setOptionLists([...optionLists, { name: importListName.trim(), options }]);
            setImportListName('');
            setImportListText('');
            setImportFileOptions([]);
            setImportParsedRows([]);
            setImportColumnIndex(0);
            setScreen('editGameLists');
          }}
          style={{ marginTop: 8 }}
        >
          Save
        </button>
        <button
          onClick={() => {
            if (importListName.trim() || importListText.trim()) {
              setDiscardImportModalOpen(true);
            } else {
              setImportListName('');
              setImportListText('');
              setImportFileOptions([]);
              setImportParsedRows([]);
              setImportColumnIndex(0);
              setScreen('editGameLists');
            }
          }}
          style={{ marginLeft: 8 }}
        >
          Cancel
        </button>
        {discardImportModalOpen && (
          <div className="modal-overlay" onClick={() => setDiscardImportModalOpen(false)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <p>You have unsaved import data. Are you sure you want to discard it?</p>
              <button
                onClick={() => {
                  setImportListName('');
                  setImportListText('');
                  setImportFileOptions([]);
                  setImportParsedRows([]);
                  setImportColumnIndex(0);
                  setDiscardImportModalOpen(false);
                  setScreen('editGameLists');
                }}
                style={{ marginRight: 10 }}
              >
                Yes, Discard
              </button>
              <button onClick={() => setDiscardImportModalOpen(false)}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // --- Customize ---
  if (screen === 'customize') {

    return (
      <div className="centered-form">
        <h1>{editIndex !== null ? 'Edit Game Option List' : 'Add New Options List'}</h1>
        <input
          type="text"
          className="input-textbox"
          value={listName}
          onChange={e => setListName(e.target.value)}
          placeholder="List Name"
          style={{ width: '200px' }}
        />
        <div className="options-list-scrollbox">
          <ul>
            {customList.map((opt, idx) => (
              <li
                key={idx}
                onContextMenu={e => handleContextMenu(e, idx)}
                onTouchStart={() => handleTouchStart(idx)}
                onTouchEnd={handleTouchEnd}
                style={{ cursor: 'pointer', padding: '4px 0' }}
                title="Long-press or right-click for options"
              >
                {opt.value}
              </li>
            ))}
          </ul>
        </div>
        <div className="options-list-count">
          {customList.length} bingo ball{customList.length === 1 ? '' : 's'}
        </div>
        <div>
          <input
            type="text"
            className="input-textbox"
            value={customInput}
            onChange={e => setCustomInput(e.target.value)}
            placeholder="Enter option"
            style={{ width: '200px' }}
          />
          <button onClick={handleClearInput}>✗</button>
          <button onClick={handleAddOption} disabled={customInput.trim().length === 0}>✔</button>
        </div>
        <button
          onClick={handleSaveList}
          disabled={customList.length === 0 || listName.trim().length === 0}
        >
          Save Option List
        </button>
        <button
          onClick={() => {
            if (unsavedChanges) {
              setShowUnsavedModal(true);
            } else {
              setScreen('editGameLists');
            }
          }}
        >
          Back to List
        </button>
        <button
          className="delete-game-btn"
          onClick={() => setDeleteModalOpen(true)}
        >
          Delete Game
        </button>

        {showUnsavedModal && (
          <div className="modal-overlay" onClick={() => setShowUnsavedModal(false)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <p>You have unsaved changes. What would you like to do?</p>
              <button
                onClick={() => {
                  handleSaveList();
                  setShowUnsavedModal(false);
                  setScreen('editGameLists');
                }}
                style={{ marginRight: 10 }}
              >
                Save & Continue
              </button>
              <button onClick={() => {
                setShowUnsavedModal(false);
                setScreen('editGameLists');
              }}>Discard Changes</button>
              <button onClick={() => setShowUnsavedModal(false)} style={{ marginRight: 10 }}>Cancel</button>
            </div>
          </div>
        )}
        {deleteModalOpen && (
          <div className="modal-overlay" onClick={() => setDeleteModalOpen(false)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <p>Are you sure you want to delete this game?</p>
              <button
                className="delete-game-btn"
                style={{ marginRight: 10 }}
                onClick={() => handleDeleteGame(editListIndex!)}
              >
                Yes, Delete
              </button>
              <button onClick={() => setDeleteModalOpen(false)}>Cancel</button>
            </div>
          </div>
        )}

        {modalOpen && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <button onClick={handleEditFromModal} style={{ marginRight: 10 }}>Edit</button>
              <button onClick={handleDeleteFromModal}>Delete</button>
              <div style={{ marginTop: 10 }}>
                <button onClick={closeModal}>Cancel</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // --- Generate Cards ---
  if (screen === 'generateCards') {
    return (
      <div className="centered-form" style={{ maxHeight: '100vh', overflow: 'hidden' }}>
        <h1>Generate BINGO Cards</h1>
        <h2>Select a Game</h2>
        <div className="game-listbox">
          <ul>
            {optionLists.map((list, idx) => (
              <li key={idx}>
                <button
                  className={cardsGameIdx === idx ? 'selected-btn' : ''}
                  onClick={() => {
                     setCardsGameIdx(idx);
                  }}
                >
                  {list.name}
                </button>
              </li>
            ))}
          </ul>
        </div>
        <h2>Number of Cards</h2>
        <input
          type="number"
          className="input-textbox"
          min={1}
          max={99}
          value={numCardsInput}
          onChange={e => {
            const val = e.target.value;
            setNumCardsInput(val);
            const num = Number(val);
            if (val === '' || isNaN(num)) {
              // Don't update numCards if input is empty or invalid
              return;
            }
            setNumCards(Math.max(1, Math.min(99, num)));
          }}
          style={{ width: '60px' }}
        />
        <div style={{ marginTop: 16 }}>
          <button
            disabled={cardsGameIdx === null}
            onClick={() => {
              generateCards();
              setScreen('viewCards');
            }}
          >
            Generate
          </button>
          <button onClick={() => setScreen('menu')} style={{ marginLeft: 8 }}>
            Back to Main Menu
          </button>
        </div>        
      </div>
    );
  }

  // --- View Cards Screen ---
  if (screen === 'viewCards') {
    return (
      <div className="centered-form" style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        {/* Header and buttons always visible */}
        <div style={{ position: 'sticky', top: 0, background: 'var(--primary-bg)', zIndex: 2, paddingTop: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <button onClick={() => setScreen('generateCards')}>Back</button>
            <button onClick={handleSavePDF}>Save to PDF</button>
          </div>
        </div>
        {/* Card previews scroll horizontally */}
        <div style={{ flex: 1, width: '100%', overflowY: 'auto', paddingTop: 8 }}>
          {generatedCards.length > 0 ? (
            generatedCards.map((card, idx) => {
              const isMobile = Capacitor.getPlatform() !== 'web';
              const cellStyle: React.CSSProperties = {
                border: '1px solid #333',
                padding: isMobile ? '4px' : '8px',
                textAlign: 'center' as const,
                background: 'var(--card-bg)',
                maxWidth: isMobile ? 48 : 100,
                minWidth: isMobile ? 48 : 100,
                wordBreak: 'break-word',
                whiteSpace: 'normal',
                fontSize: isMobile ? '0.65rem' : '1rem',
              };
              const freeCellStyle: React.CSSProperties = {
                ...cellStyle,
                background: 'var(--success-color)',
                fontWeight: 'bold',
              };
              return (
                <div key={idx} style={{ marginBottom: 24 }}>
                  <h3>{optionLists[cardsGameIdx ?? 0].name} Card {idx + 1}</h3>
                  <div className="bingo-card-scroll-container">
                    <table className="bingo-card-table" style={{ borderCollapse: 'collapse' }}>
                      <tbody>
                        {card.map((row, rIdx) => (
                          <tr key={rIdx}>
                            {row.map((cell, cIdx) => (
                              <td
                                key={cIdx}
                                style={cell === 'FREE' ? freeCellStyle : cellStyle}
                              >
                                {cell}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })
          ) : (
            <div>No cards generated.</div>
          )}
        </div>
      </div>
    );
  }

  // --- Game screen ---
  if (screen === 'game') {
    const options = selectedListIndex !== null ? optionLists[selectedListIndex].options : [];
    const gameName = selectedListIndex !== null ? optionLists[selectedListIndex].name : '';
    const remainingCount =
      options.length -
      calledIds.length;

    return (
      <div className="centered-form">
        <h1>{gameName} Tracker</h1>
        <button className="primary-btn" onClick={handleCall} disabled={calledIds.length === options.length}>
          Call Next Ball
        </button>
        <ResetButton onReset={() => setResetModalOpen(true)} />
        <button className="primary-btn" onClick={() => setMainMenuModalOpen(true)}>
          Back to Main Menu
        </button>
        {/* Reset Confirmation Modal */}
        {resetModalOpen && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h2>Reset Game?</h2>
              <p>Are you sure you want to reset the game? This will clear all called balls.</p>
              <button onClick={() => { handleReset(); setResetModalOpen(false); }} style={{ marginRight: 12 }}>Yes, Reset</button>
              <button onClick={() => setResetModalOpen(false)}>Cancel</button>
            </div>
          </div>
        )}
        {/* Main Menu Confirmation Modal */}
        {mainMenuModalOpen && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h2>Leave Game?</h2>
              <p>If you return to the main menu, your current game will be lost. Are you sure?</p>
              <button onClick={() => { setScreen('menu'); setMainMenuModalOpen(false); }} style={{ marginRight: 12 }}>
                Yes, Back to Main Menu
              </button>
              <button onClick={() => setMainMenuModalOpen(false)}>Cancel</button>
            </div>
          </div>
        )}
        <div>
          <h2 style={{textAlign: 'center'}}>Current:</h2>
          <div  style={{textAlign: 'center'}}>
            <strong className="last-called-text">
              {lastCalledText || '---'}
            </strong>
          </div>
        </div>
        <div className="options-list-count">
          <br/>
          Remaining: {remainingCount >= 0 ? remainingCount : 0}
        </div>
        <div>
          <h2 style={{textAlign: 'center'}}>Called Balls:</h2>
          <div className="options-list-scrollbox">
            <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
              {[...calledIds].reverse().map(id => <li key={id}>{optionMap.get(id) ?? id}</li>)}
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default App;